import { formatDistanceToNow } from 'date-fns';
import type { FC } from 'react';
import { memo, useMemo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import type { ProposalListItem } from '@/lib/proposals/core/types';
import { getOnboardingConfig } from '@/lib/proposals/utils/onboardingConfig';
import { cn } from '@/lib/utils';

interface OnboardingActionCardProps {
  action: ProposalListItem;
  isSelected?: boolean;
  isNew?: boolean;
  onClick?: () => void;
  onMouseDown?: () => void;
}

const OnboardingActionCardComponent: FC<OnboardingActionCardProps> = ({
  action,
  isSelected = false,
  isNew = false,
  onClick,
  onMouseDown,
}) => {
  const config = getOnboardingConfig(action);

  // Memoize date formatting to avoid expensive recalculation on every render
  // Note: This is a snapshot timestamp that won't auto-refresh. Acceptable for this use case since parent re-renders on data changes.
  const timeAgo = useMemo(
    () =>
      formatDistanceToNow(new Date(action.created_at), {
        addSuffix: true,
      }),
    [action.created_at]
  );

  return (
    <button
      type='button'
      onMouseDown={onMouseDown}
      onClick={onClick}
      aria-current={isSelected ? 'page' : undefined}
      aria-label={`${action.title} - ${config.timeEstimate}`}
      className={cn(
        // border-t, never border-y. Rows in this list are virtualised and
        // absolutely positioned, so a divider has to come from the rows
        // themselves — which only works if every row draws exactly one, on the
        // same side. With border-y each card contributed a bottom border and
        // its neighbour a top border, stacking into a 2px rule between cards
        // while the first and last edges stayed 1px.
        'w-full text-left bg-bg-primary border-t border-border-secondary p-4 relative',
        'hover:bg-bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-brand-primary focus-visible:z-10',
        'min-h-[120px]', // Consistent card height
        isSelected && 'bg-bg-secondary'
      )}
    >
      <div className='flex items-start gap-3'>
        {/* Left: Featured Icon */}
        <FeaturedIcon
          icon={config.icon}
          size='sm'
          theme='light-outline'
          color={config.color}
          className={isNew ? 'proposal-icon' : undefined}
        />

        {/* Center: Content */}
        <div className='flex-1 min-w-0'>
          {/* Title */}
          <h3 className='text-base font-semibold text-fg-primary mb-1'>
            {action.title}
          </h3>

          {/* Subtitle/Description */}
          <p className='text-sm text-fg-secondary mb-2'>{action.description}</p>

          {/* Metadata */}
          <p className='text-xs text-fg-tertiary'>
            Created by Ahoy • {timeAgo}
          </p>
        </div>

        {/* Top-right: Time Badge */}
        <Badge type='pill-color' color='gray' size='sm'>
          {config.timeEstimate}
        </Badge>
      </div>
    </button>
  );
};

// Memoize to prevent unnecessary re-renders in virtualized lists
export const OnboardingActionCard = memo(OnboardingActionCardComponent);
